import type { TemplateStatsDto, TrialToPaid } from '@/services/formTemplates';

// Display formatting for the Templates section's stats; pure, so it is tested in node.

/** What a number shows when there is nothing to count; the Templates pages draw it muted. */
export const DASH = '—';

export function formatRate(rate: number): string {
  return `${rate.toFixed(1)}%`;
}

/** A rate over unique visitors; a window without visitors has no rate, not 0.0%. */
export function formatVisitorRate(rate: number, uniqueVisitors: number): string {
  return uniqueVisitors === 0 ? DASH : formatRate(rate);
}

export function formatCohort(hit: number, eligible: number): string {
  if (eligible === 0) return DASH;
  return `${hit} / ${eligible} (${Math.round((hit / eligible) * 100)}%)`;
}

export function formatDays(days: number | null): string {
  if (days === null) return DASH;
  return days === 1 ? '1 day' : `${days} days`;
}

export function formatTrialToPaid(t: TrialToPaid): { rate: string; detail: string } {
  if (t.agencies === 0) return { rate: DASH, detail: 'No trial has ended yet' };
  return {
    rate: formatRate(t.rate),
    detail: `${t.paying} of ${t.agencies} ${t.agencies === 1 ? 'agency pays' : 'agencies pay'}`,
  };
}

export type LiftTone = 'up' | 'down' | 'flat' | 'none';

/**
 * Template users' trial-to-paid rate minus all trials', as the gap between the two rates ("+14.5%"), the way the
 * Changelog's cohort lift reads. Not "pts": the product ruled that jargon.
 */
export function formatLift(templateUsers: TrialToPaid, allTrials: TrialToPaid): { text: string; tone: LiftTone } {
  if (templateUsers.agencies === 0 || allTrials.agencies === 0) return { text: DASH, tone: 'none' };
  const d = Math.round((templateUsers.rate - allTrials.rate) * 10) / 10;
  const sign = d > 0 ? '+' : d < 0 ? '-' : '';
  return {
    text: `${sign}${Math.abs(d).toFixed(1)}%`,
    tone: d > 0 ? 'up' : d < 0 ? 'down' : 'flat',
  };
}

export interface StatRow {
  key: string;
  label: string;
  value: string;
  hint: string;
}

export function templateStatRows(stats: TemplateStatsDto): StatRow[] {
  const count = (n: number) => n.toLocaleString('en-US');
  return [
    { key: 'imports', label: 'Imports', value: count(stats.imports), hint: 'Copies customers made' },
    { key: 'agencies', label: 'Agencies', value: count(stats.agencies), hint: 'Different agencies' },
    { key: 'copiesKept', label: 'Copies kept', value: count(stats.copiesKept), hint: 'Copies not deleted' },
    {
      key: 'publishedWithin7d',
      label: 'Published within 7 days',
      value: formatCohort(stats.publishedWithin7d, stats.eligible7d),
      hint: 'Of imports at least 7 days old',
    },
    {
      key: 'leadWithin14d',
      label: 'Lead within 14 days',
      value: formatCohort(stats.leadWithin14d, stats.eligible14d),
      hint: 'Of kept copies at least 14 days old',
    },
    { key: 'uniqueVisitors', label: 'Unique visitors', value: count(stats.uniqueVisitors), hint: 'Across all copies' },
    { key: 'completedLeads', label: 'Completed leads', value: count(stats.completedLeads), hint: 'Partial leads left out' },
    {
      key: 'completedOptInRate',
      label: 'Completed opt-in rate',
      value: formatVisitorRate(stats.completedOptInRate, stats.uniqueVisitors),
      hint: 'Completed leads / unique visitors',
    },
    { key: 'bookings', label: 'Bookings', value: count(stats.bookings), hint: 'Cancelled bookings left out' },
    {
      key: 'bookingsPerVisitor',
      label: 'Bookings per visitor',
      value: formatVisitorRate(stats.bookingsPerVisitor, stats.uniqueVisitors),
      hint: 'Bookings / unique visitors',
    },
    {
      key: 'daysToFirstPublish',
      label: 'Days to first publish',
      value: formatDays(stats.medianDaysToFirstPublish),
      hint: 'Median, published copies only',
    },
  ];
}
